import React from 'react';
import { BlankModal, HStack, ModalBody, ModalFooter, ModalHeader, useToast } from '@cad-arc';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { WorkspacesResource, WorkspaceTemplateOption } from '@mark43/cad-api';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { ManageWorkspaceList } from './manage-workspace-list';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';

type DefaultDepartmentTemplateForm = {
    defaultDepartmentTemplate: WorkspaceTemplateOption | null;
};

export type ManageDepartmentWorkspacesModalComponentType = typeof ManageDepartmentWorkspacesModal;
export function ManageDepartmentWorkspacesModal(props: {
    defaultDepartmentTemplate?: WorkspaceTemplateOption;
    workspaceOptions: WorkspaceTemplateOption[];
}): React.ReactElement {
    const [localDefaultDepartmentTemplate, setLocalDefaultDepartmentTemplate] = React.useState<
        WorkspaceTemplateOption | undefined
    >(props.defaultDepartmentTemplate);
    const [localWorkspaceOptions, setLocalWorkspaceOptions] = React.useState<
        WorkspaceTemplateOption[]
    >(props.workspaceOptions);
    const modalControls = useFullscreenModalControls();
    const onCancel = React.useCallback(() => {
        modalControls.close();
    }, [modalControls]);
    const apiClient = useApiClient();
    const form = useForm<DefaultDepartmentTemplateForm>({
        defaultValues: { defaultDepartmentTemplate: localDefaultDepartmentTemplate ?? null },
        mode: 'onBlur',
    });
    const toast = useToast();
    const mutation = useMutation(
        (args: DefaultDepartmentTemplateForm) => {
            if (!args.defaultDepartmentTemplate) {
                return Promise.reject();
            }
            return apiClient.request<WorkspacesResource.PromoteDepartmentWorkspaceToDefault>({
                method: 'POST',
                url: `workspaces/department/default/${args.defaultDepartmentTemplate.value}/`,
            });
        },
        {
            onSuccess() {
                onCancel();
            },
            onError() {
                toast({
                    status: 'error',
                    description: 'Failed to save default department template.',
                });
            },
        }
    );

    return (
        <IntegratedFormProvider mutation={mutation} form={form}>
            <BlankModal size="lg" onClose={onCancel} isOpen={true}>
                <ModalHeader>Manage Department Templates</ModalHeader>
                <ModalBody>
                    <FormBody>
                        <>
                            <FormSelect
                                label="Default Workspace"
                                name="defaultDepartmentTemplate"
                                control={form.control}
                                rules={{ required: 'Required' }}
                                options={localWorkspaceOptions}
                            />
                            <ManageWorkspaceList
                                onRename={(workspaceTemplateOption) => {
                                    if (
                                        workspaceTemplateOption.value ===
                                        localDefaultDepartmentTemplate?.value
                                    ) {
                                        setLocalDefaultDepartmentTemplate(workspaceTemplateOption);
                                        form.setValue(
                                            'defaultDepartmentTemplate',
                                            workspaceTemplateOption
                                        );
                                    }
                                }}
                                defaultDepartmentTemplate={props.defaultDepartmentTemplate}
                                workspaceOptions={localWorkspaceOptions}
                                setWorkspaceOptions={setLocalWorkspaceOptions}
                                workspaceTemplateType="DEPARTMENT"
                            />
                        </>
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <HStack>
                        <FormCancelButton onClick={onCancel} />
                        <FormSaveButton />
                    </HStack>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
}
